import { createRootRoute, createRoute, createRouter, Outlet, redirect, useRouterState } from '@tanstack/react-router'
import { lazy, Suspense, useEffect } from 'react'
import { useAuthStore } from '@/stores/auth-store'
import { useBrand } from '@/providers/brand-provider'
import { RouteErrorComponent } from '@/components/error-boundary'

const ShowcasePage = lazy(() => import('./pages/showcase-page'))
const FieldShowcasePage = lazy(() => import('./pages/field-showcase-page'))
const LandingPage = lazy(() => import('./pages/landing-page'))
const GGLandingPage = lazy(() => import('./pages/gg-landing-page'))
const LoginPage = lazy(() => import('./pages/login-page'))
const PrequalPage = lazy(() => import('./pages/prequal-page'))
const DashboardPage = lazy(() => import('./pages/dashboard-page'))
const DatabasePage = lazy(() => import('./pages/database-page'))
const ProfileDetailPage = lazy(() => import('./pages/profile-detail-page'))
const FormPage = lazy(() => import('./pages/form-page'))
const NotFoundPage = lazy(() => import('./pages/not-found-page'))
const AppShell = lazy(() => import('./components/app-shell'))

function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-background">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      }
    >
      {children}
    </Suspense>
  )
}

function ScrollRestoration() {
  const pathname = useRouterState({ select: s => s.location.pathname })
  useEffect(() => {
    if (pathname) window.scrollTo(0, 0)
  }, [pathname])
  return null
}

function requireAuth() {
  const { isAuthenticated } = useAuthStore.getState()
  if (!isAuthenticated) {
    throw redirect({ to: '/login', search: { redirect: window.location.pathname } })
  }
}

const rootRoute = createRootRoute({
  component: () => (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  ),
  notFoundComponent: () => (
    <LazyPage>
      <NotFoundPage />
    </LazyPage>
  ),
})

// ─── Public Routes ──────────────────────────────────────────────────────────

function BrandAwareLanding() {
  const { brandId } = useBrand()
  return brandId === 'gg' ? <GGLandingPage /> : <LandingPage />
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => (
    <LazyPage>
      <BrandAwareLanding />
    </LazyPage>
  ),
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: () => (
    <LazyPage>
      <LoginPage />
    </LazyPage>
  ),
})

const prequalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prequal',
  component: () => (
    <LazyPage>
      <PrequalPage />
    </LazyPage>
  ),
})

const showcaseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/showcase',
  component: () => (
    <LazyPage>
      <ShowcasePage />
    </LazyPage>
  ),
})

const fieldShowcaseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/showcase/fields',
  component: () => (
    <LazyPage>
      <FieldShowcasePage />
    </LazyPage>
  ),
})

// ─── Authenticated Layout (AppShell wraps all protected routes) ─────────────

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: requireAuth,
  component: () => (
    <LazyPage>
      <AppShell />
    </LazyPage>
  ),
})

const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/dashboard',
  component: () => (
    <LazyPage>
      <DashboardPage />
    </LazyPage>
  ),
})

const databaseRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/database',
  component: () => (
    <LazyPage>
      <DatabasePage />
    </LazyPage>
  ),
})

const profileDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/profile/$id',
  component: () => (
    <LazyPage>
      <ProfileDetailPage />
    </LazyPage>
  ),
})

// ─── Form Routes (NOT auth-gated - form checks allowAnonymous internally) ───
// /form/{program}/{formId}/{layout}/{instance}
// layout defaults to 'default', instance is optional (present when editing existing data)

const formWithInstanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/form/$program/$formId/$layoutName/$instanceId',
  component: () => (
    <LazyPage>
      <FormPage />
    </LazyPage>
  ),
})

const formWithLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/form/$program/$formId/$layoutName',
  component: () => (
    <LazyPage>
      <FormPage />
    </LazyPage>
  ),
})

const formMinimalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/form/$program/$formId',
  component: () => (
    <LazyPage>
      <FormPage />
    </LazyPage>
  ),
})

// ─── Route Tree ─────────────────────────────────────────────────────────────

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  prequalRoute,
  showcaseRoute,
  fieldShowcaseRoute,
  formWithInstanceRoute,
  formWithLayoutRoute,
  formMinimalRoute,
  authenticatedRoute.addChildren([dashboardRoute, databaseRoute, profileDetailRoute]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultErrorComponent: RouteErrorComponent,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
